import { navigate } from 'gatsby';

import type { ClubInformation } from 'src/enums/club-information';
import type { Language } from 'src/enums/language';
import { trimPath } from 'src/helpers/path-matching-helper';
import { updateUrlPreservingQueryParams } from 'src/helpers/query-string-helper';
import { translatePath } from 'src/translators/links-translator';

import { getClubInformation } from '../content/club-look-ups-by-id';
import type { BuildType } from '../enums/build/build-type';
import { getLanguageUrlPrefixForBuildType } from '../enums/build/build-type';

import { getSiteBaseUrl } from './config-helper';
import { getBestLanguageMatch } from './language-helper';

export function navigateToGlobalPage(
  path: string,
  language: Language,
  buildType: BuildType,
  state?: any
) {
  const globalPagePath: string = getGlobalPagePath(path, language, buildType);
  navigate(updateUrlPreservingQueryParams(globalPagePath), { state: state });
}

export function navigateToInternalClubPage(
  path: string,
  siteId: string,
  language: Language,
  options?: { state?: any; excludedQueryParams?: string[]; newQueryParams?: [string, string][] }
) {
  const clubPagePath: string = getClubPagePath(path, siteId, language);
  navigate(
    updateUrlPreservingQueryParams(clubPagePath, {
      excludedQueryParams: options?.excludedQueryParams,
      newQueryParams: options?.newQueryParams,
    }),
    { state: options?.state }
  );
}

export function navigateToExternalPage(url: string) {
  window.location.href = url;
}

export function navigateToExternalClubPage(
  path: string,
  siteId: string,
  language: Language,
  options?: { excludedQueryParams?: string[]; newQueryParams?: [string, string][] }
) {
  const clubPagePath = getAbsoluteClubPagePath(path, siteId, language);
  navigateToExternalPage(
    updateUrlPreservingQueryParams(clubPagePath, {
      excludedQueryParams: options?.excludedQueryParams,
      newQueryParams: options?.newQueryParams,
    })
  );
}

export function navigateToExternalClubPageNewTab(
  path: string,
  siteId: string,
  language: Language,
  options?: { excludedQueryParams?: string[] }
) {
  const clubPagePath = getAbsoluteClubPagePath(path, siteId, language);
  openPageInNewTab(
    updateUrlPreservingQueryParams(clubPagePath, {
      excludedQueryParams: options?.excludedQueryParams,
    })
  );
}

export function navigateToExternalPageWithoutHistory(url: string) {
  return document.location.replace(url);
}

export const preparePath = (path: string) => path.replace(/\/\/\//g, '/').replace(/\/\//g, '/');

export function getClubPagePath(basePath: string, siteId: string, language: Language) {
  const clubInformation = getClubInformation(siteId);
  return getClubPagePathFromClubInformation(basePath, clubInformation, language);
}

export function getClubPagePathFromClubInformation(
  basePath: string,
  clubInformation: ClubInformation,
  language: Language
) {
  const { buildType, languagesOverride, hyphenatedClubName: clubNameUrl } = clubInformation;
  const bestMatchLanguage = getBestLanguageMatch(language, buildType, languagesOverride);

  const translatedBasePath = translatePath(basePath, bestMatchLanguage);
  const languagePrefix = getLanguageUrlPrefixForBuildType(bestMatchLanguage, buildType);
  const path = `/${languagePrefix}/clubs/${clubNameUrl}/${translatedBasePath}/`;

  return preparePath(path);
}

export function getAbsoluteClubPagePath(basePath: string, siteId: string, language: Language) {
  const clubInformation = getClubInformation(siteId);
  return getAbsoluteClubPagePathFromClubInformation(basePath, clubInformation, language);
}

export function getAbsoluteClubPagePathFromClubInformation(
  basePath: string,
  clubInformation: ClubInformation,
  language: Language
) {
  const { buildType, siteId } = clubInformation;
  const clubPagePath = getClubPagePath(basePath, siteId, language);
  const siteBase = getSiteBaseUrl(buildType);

  return `${siteBase}${clubPagePath}`;
}

export function getGlobalPagePath(basePath: string, language: Language, buildType: BuildType) {
  const translatedBasePath = translatePath(basePath, language);
  const languagePrefix = getLanguageUrlPrefixForBuildType(language, buildType);
  const path = `/${languagePrefix}/${translatedBasePath}/`;

  return preparePath(path);
}

export const getSiteUrlForPath = (buildType: BuildType, path: string) => {
  const baseUrl = trimPath(getSiteBaseUrl(buildType));
  const normalisedPath = preparePath(`/${trimPath(path)}/`);

  return `${baseUrl}${normalisedPath}`;
};

export function openPageInNewTab(url: string) {
  window.open(url);
}

export function removeQueryParamsFromUrl(queryParams: string[]) {
  if (typeof window === 'undefined') {
    return;
  }

  const newPath = updateUrlPreservingQueryParams(window.location.pathname, {
    excludedQueryParams: queryParams,
  });
  navigate(newPath, { replace: true, state: window.history.state });
}

export const navigateToSalesApp = () => {
  navigateToExternalPageWithoutHistory(process.env.GATSBY_SALES_APP_URL!);
};

export const isGlobalLinkCurrentPage = (
  link: string,
  language: Language,
  buildType: BuildType,
  basePath: string
) => {
  return getGlobalPagePath(link, language, buildType) === translatePath(basePath, language);
};

export const isClubLinkCurrentPage = (
  link: string,
  language: Language,
  siteId: string,
  basePath: string
) => {
  return getClubPagePath(link, siteId, language) === translatePath(basePath, language);
};
